import { useSyncExternalStore } from 'react';
import {
  DEFAULT_SIDEBAR_VIEW,
  type SidebarGroupBy,
  type SidebarShow,
  type SidebarView,
} from '@/lib/desktop-shell/sidebar-model';

/*
 * What the sidebar remembers on this device: its View (D70), the favourites the user laid out
 * (which tiles, their order, how many a row holds) and how often each app is opened (the default
 * favourites). Pinned conversations are the server's (`conversation-pins`).
 */

/** One View for the whole sidebar; the old per-workspace keys (`…view.<scope>`) are not read. */
const VIEW_KEY = 'crowdex.sidebar.view';

const FAVORITES_KEY = 'crowdex.sidebar.favorites';
const APP_USAGE_KEY = 'crowdex.sidebar.app-usage';

const listeners = new Set<() => void>();
const cache = new Map<string, unknown>();

function read<T>(key: string, parse: (value: unknown) => T): T {
  if (cache.has(key)) return cache.get(key) as T;
  let value: T;
  try {
    value = parse(JSON.parse(globalThis.localStorage?.getItem(key) ?? 'null'));
  } catch {
    value = parse(null);
  }
  cache.set(key, value);
  return value;
}

function write(key: string, value: unknown): void {
  cache.set(key, value);
  try {
    globalThis.localStorage?.setItem(key, JSON.stringify(value));
  } catch {
    // Storage may be unavailable: the choice lasts for this session.
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function usePreference<T>(key: string, parse: (value: unknown) => T): T {
  return useSyncExternalStore(
    subscribe,
    () => read(key, parse),
    () => read(key, parse),
  );
}

/** Forgets what was read, for tests that change the storage under it. */
export function resetSidebarPreferences(): void {
  cache.clear();
  for (const listener of listeners) listener();
}

const GROUP_BY: readonly SidebarGroupBy[] = ['assistant', 'repository', 'none'];
const SHOW: readonly SidebarShow[] = ['all', 'running', 'pull-request'];
/** Former names of a grouping, as a View stored before the rename holds them. */
const FORMER_GROUP_BY: Readonly<Record<string, SidebarGroupBy>> = { project: 'assistant' };

function parseView(value: unknown): SidebarView {
  const stored = (value ?? {}) as Partial<Record<keyof SidebarView, unknown>>;
  const groupBy =
    typeof stored.groupBy === 'string'
      ? (FORMER_GROUP_BY[stored.groupBy] ?? stored.groupBy)
      : stored.groupBy;
  return {
    groupBy: GROUP_BY.includes(groupBy as SidebarGroupBy)
      ? (groupBy as SidebarGroupBy)
      : DEFAULT_SIDEBAR_VIEW.groupBy,
    show: SHOW.includes(stored.show as SidebarShow)
      ? (stored.show as SidebarShow)
      : DEFAULT_SIDEBAR_VIEW.show,
  };
}

function parseStrings(value: unknown): readonly string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string')
    : [];
}

function parseUsage(value: unknown): Readonly<Record<string, number>> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.fromEntries(
    Object.entries(value).filter(
      (entry): entry is [string, number] => typeof entry[1] === 'number',
    ),
  );
}

/** The sidebar's View, and how to change it; remembered on this device. */
export function useSidebarView() {
  const view = usePreference(VIEW_KEY, parseView);
  return {
    view,
    setView: (next: Partial<SidebarView>) => write(VIEW_KEY, { ...view, ...next }),
  };
}

/** The favourites' Library tile, among the apps' views. All apps is not an entry: always last. */
export const LIBRARY_FAVORITE = 'library';

/** How many tiles a row of the favourites holds, as the user may choose it. */
export const FAVORITE_COLUMNS = { desktop: [3, 4, 5], phone: [3, 4] } as const;
export type FavoritesDensity = keyof typeof FAVORITE_COLUMNS;
const DEFAULT_COLUMNS: Readonly<Record<FavoritesDensity, number>> = { desktop: 4, phone: 4 };
/** The apps the favourites hold until the user edits them (the most opened). */
const DEFAULT_APP_COUNT = 2;

/**
 * The favourites as the user laid them out: their entries in order (Library, apps), null until
 * edited, and the tiles of a row on each density.
 */
export type FavoritesLayout = {
  entries: readonly string[] | null;
  columns: Readonly<Record<FavoritesDensity, number>>;
};

function parseColumns(value: unknown): Readonly<Record<FavoritesDensity, number>> {
  const stored = (value ?? {}) as Partial<Record<FavoritesDensity, unknown>>;
  const pick = (density: FavoritesDensity) =>
    (FAVORITE_COLUMNS[density] as readonly number[]).includes(stored[density] as number)
      ? (stored[density] as number)
      : DEFAULT_COLUMNS[density];
  return { desktop: pick('desktop'), phone: pick('phone') };
}

function unique(entries: readonly string[]): string[] {
  return [...new Set(entries)];
}

/** Reads the stored favourites; null entries until the user edits them. */
export function parseFavorites(value: unknown): FavoritesLayout {
  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    const stored = value as { entries?: unknown; columns?: unknown };
    return {
      entries: Array.isArray(stored.entries) ? unique(parseStrings(stored.entries)) : null,
      columns: parseColumns(stored.columns),
    };
  }
  return { entries: null, columns: DEFAULT_COLUMNS };
}

const readFavorites = () => read(FAVORITES_KEY, parseFavorites);

/**
 * The favourites shown, in order: those the user laid out, among the apps still there; before
 * any edit, Library and the two most opened apps (the order of `views` breaking ties,
 * `fallback` first when nothing was opened yet).
 */
export function resolveFavorites(
  views: readonly string[],
  entries: readonly string[] | null,
  usage: Readonly<Record<string, number>>,
  fallback: readonly string[] = [],
): string[] {
  const available = new Set([LIBRARY_FAVORITE, ...views]);
  if (entries !== null) return entries.filter((entry) => available.has(entry));
  const rank = (view: string) => {
    const index = fallback.indexOf(view);
    return index === -1 ? fallback.length + views.indexOf(view) : index;
  };
  const apps = [...views]
    .sort((left, right) => (usage[right] ?? 0) - (usage[left] ?? 0) || rank(left) - rank(right))
    .slice(0, DEFAULT_APP_COUNT);
  return [LIBRARY_FAVORITE, ...apps];
}

/** The favourites as laid out on this device (see `useFavorites` for what they show). */
export function useFavoritesLayout(): FavoritesLayout {
  return usePreference(FAVORITES_KEY, parseFavorites);
}

function writeFavorites(next: FavoritesLayout): void {
  write(FAVORITES_KEY, next);
}

/**
 * Lays out the favourites: `next` in place of `shown` (the entries on screen, which an edit
 * starts from). The entries hidden meanwhile (an app disabled) are kept, after them.
 */
function setFavoriteEntries(shown: readonly string[], next: readonly string[]): void {
  const layout = readFavorites();
  const hidden = (layout.entries ?? []).filter((entry) => !shown.includes(entry));
  writeFavorites({ ...layout, entries: unique([...next, ...hidden]) });
}

/** Adds an entry at the end (before All apps); nothing if it is there. */
export function addFavorite(shown: readonly string[], entry: string): string[] {
  const next = shown.includes(entry) ? [...shown] : [...shown, entry];
  setFavoriteEntries(shown, next);
  return next;
}

export function removeFavorite(shown: readonly string[], entry: string): string[] {
  const next = shown.filter((candidate) => candidate !== entry);
  setFavoriteEntries(shown, next);
  return next;
}

/** Moves an entry to `index` (clamped to the list), the others closing up. */
export function moveFavorite(shown: readonly string[], entry: string, index: number): string[] {
  const from = shown.indexOf(entry);
  if (from === -1) return [...shown];
  const next = shown.filter((candidate) => candidate !== entry);
  next.splice(Math.max(0, Math.min(index, next.length)), 0, entry);
  setFavoriteEntries(shown, next);
  return next;
}

export function setFavoriteColumns(density: FavoritesDensity, columns: number): void {
  const layout = readFavorites();
  writeFavorites({ ...layout, columns: parseColumns({ ...layout.columns, [density]: columns }) });
}

export function useAppUsage(): Readonly<Record<string, number>> {
  return usePreference(APP_USAGE_KEY, parseUsage);
}

/** Counts one opening of an app, which ranks the default favourites. */
export function recordAppOpened(view: string): void {
  const usage = read(APP_USAGE_KEY, parseUsage);
  write(APP_USAGE_KEY, { ...usage, [view]: (usage[view] ?? 0) + 1 });
}
